"use client";

import { useState, useEffect } from "react";
import { ANNUAL_PRICE_CENTS_PER_LOCATION, calculateBreakdown, formatCurrency } from "@/lib/landing-pricing";
import { Building2 } from "lucide-react";

type Props = {
  initialLocations?: number;
  maxLocations?: number;
  currency?: string;
  variant?: "compact" | "full";
  onChange?: (locations: number, totalCents: number) => void;
};

export default function PricingCalculator({
  initialLocations = 1,
  maxLocations = 5,
  currency = "USD",
  variant = "compact",
  onChange,
}: Props) {
  const [locations, setLocations] = useState(initialLocations);
  const breakdown = calculateBreakdown(locations, currency);
  const annualTotalLabel = formatCurrency(ANNUAL_PRICE_CENTS_PER_LOCATION * locations, currency);

  useEffect(() => {
    onChange?.(locations, breakdown.totalCents);
  }, [locations, breakdown.totalCents, onChange]);

  const buttons = Array.from({ length: maxLocations }, (_, i) => i + 1);
  const isFull = variant === "full";

  return (
    <div className="w-full">
      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-2 text-ink-700">
          <Building2 className="h-4 w-4" aria-hidden="true" />
          <span className="text-sm font-semibold">How many locations?</span>
        </div>
        <span className="text-xs text-ink-500 tabular">
          {locations}
          {locations >= maxLocations ? "+" : ""}
        </span>
      </div>

      <div className="mb-6 flex flex-wrap gap-2" role="radiogroup" aria-label="Number of locations">
        {buttons.map((n) => {
          const selected = n === locations;
          const label = n === maxLocations ? `${n}+` : `${n}`;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setLocations(n)}
              className={[
                "flex-1 min-w-[44px] rounded-xl border-2 px-3 py-2.5 text-sm font-bold transition-colors tabular",
                selected
                  ? "border-brand-600 bg-brand-600 text-white"
                  : "border-ink-200 bg-white text-ink-700 hover:border-brand-300 hover:bg-brand-50",
              ].join(" ")}
            >
              {label}
            </button>
          );
        })}
      </div>

      <ul className="mb-5 space-y-2 border-t border-ink-100 pt-4">
        {breakdown.lineItems.map((item) => (
          <li
            key={item.position}
            className="flex items-baseline justify-between gap-3 text-sm"
          >
            <span className="text-ink-600">
              Location {item.position}
              {item.discountLabel && (
                <span className="ml-2 inline-flex items-center rounded-full bg-accent-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent-700">
                  {item.discountLabel}
                </span>
              )}
            </span>
            <span className="font-semibold text-ink-900 tabular">{item.unitLabel}</span>
          </li>
        ))}
      </ul>

      <div className="flex items-baseline justify-between border-t-2 border-ink-100 pt-4">
        <span className="text-sm font-semibold text-ink-700">Total / month</span>
        <span className={`font-display font-bold text-ink-950 tabular ${isFull ? "text-4xl" : "text-3xl"}`}>
          {breakdown.totalLabel}
        </span>
      </div>

      {currency === "USD" && (
        <p className="mt-2 text-right text-[11px] text-ink-400">
          Annual option: {annualTotalLabel}/year · Shown in USD
        </p>
      )}
    </div>
  );
}
